// reports/useReport.ts
import { useCallback, useMemo, useState } from "react";

import { getDateTypeOptions } from "./Reportconfig";
import type { ReportFilters, ReportRow, ShipmentMode } from "./Types";
import { fetchReport } from "./ReportapiService";

const EMPTY_FILTERS: ReportFilters = {
  dateType: "",
  startDate: "",
  endDate: "",
  customers: [],
  carriers: [],
  flags: [],
};

export function useReport(initialMode: ShipmentMode = "air") {
  const [mode, setModeState] = useState<ShipmentMode>(initialMode);
  const [filters, setFilters] = useState<ReportFilters>(EMPTY_FILTERS);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const setFilter = useCallback(
    <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  // Reset clears the filters for the CURRENT mode; it does not switch mode.
  const reset = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setRows([]);
    setError(null);
    setHasGenerated(false);
  }, []);

  /**
   * Switching mode:
   *  - clears Carrier (carriers differ per mode)
   *  - drops a now-invalid Date Type (ocean-only types don't exist for air)
   *  - discards the previous result set (Air/Ocean column sets differ)
   */
  const setMode = useCallback((next: ShipmentMode) => {
    const validDateTypes = getDateTypeOptions(next).map((o) => o.value);
    setModeState(next);
    setFilters((f) => ({
      ...f,
      carriers: [],
      dateType: validDateTypes.includes(f.dateType) ? f.dateType : "",
    }));
    setRows([]);
    setError(null);
    setHasGenerated(false);
  }, []);

  const dateError = useMemo(() => {
    if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
      return "End date cannot be earlier than the start date.";
    }
    return null;
  }, [filters.startDate, filters.endDate]);

  const canGenerate = useMemo(
    () => Boolean(filters.startDate && filters.endDate) && !dateError,
    [ filters.startDate, filters.endDate, dateError]
  );

  const generate = useCallback(async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReport(mode, filters);
      setRows(result.map((r, i) => ({ ...r, sno: i + 1 })));
      setHasGenerated(true);
    } catch (e) {
      setError("Failed to generate the report. Please try again.");
      setRows([]);
      setHasGenerated(true);
    } finally {
      setLoading(false);
    }
  }, [canGenerate, filters, mode]);

  return {
    mode,
    setMode,
    filters,
    setFilter,
    reset,
    generate,
    rows,
    loading,
    error,
    hasGenerated,
    canGenerate,
    dateError,
  };
}